import { useQuery, useMutation, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  varietyPricingApi,
  type CreateVarietyPricingRequest,
  type UpdateVarietyPricingRequest,
  type UpsertBatchVarietyPricingRequest,
  type VarietyPricingListQuery,
} from "../services/varietyPricingApi";

export const useVarietyPricings = () => {
  return useQuery({
    queryKey: ["varietyPricings"],
    queryFn: varietyPricingApi.getAllVarietyPricings,
  });
};

export const useActiveVarietyPricings = () => {
  return useQuery({
    queryKey: ["varietyPricings", "active"],
    queryFn: varietyPricingApi.getActiveVarietyPricings,
  });
};

export const useVarietyPricingsPaged = (query: VarietyPricingListQuery = {}) => {
  return useQuery({
    queryKey: ["varietyPricings", "paged", query],
    queryFn: () => varietyPricingApi.getVarietyPricingsPaged(query),
    placeholderData: keepPreviousData,
  });
};

export const useVarietyPricingsLookup = (query: VarietyPricingListQuery, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ["varietyPricings", "lookup", query],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => varietyPricingApi.getVarietyPricingsPaged({ ...query, pageNumber: pageParam, pageSize: 100 }),
    getNextPageParam: (lastPage) => lastPage.pageNumber < lastPage.totalPages ? lastPage.pageNumber + 1 : undefined,
  });

export const useVarietyPricingByCropType = (cropTypeId: string) => {
  return useQuery({
    queryKey: ["varietyPricings", "cropType", cropTypeId],
    queryFn: () => varietyPricingApi.getVarietyPricingByCropType(cropTypeId),
    enabled: !!cropTypeId,
  });
};

export const useVarietyPricingByCropTypeAndVariety = (
  cropTypeId: string,
  cropVarietyId?: string
) => {
  return useQuery({
    queryKey: ["varietyPricings", "cropType", cropTypeId, "variety", cropVarietyId],
    queryFn: () =>
      varietyPricingApi.getVarietyPricingByCropTypeAndVariety(
        cropTypeId,
        cropVarietyId
      ),
    enabled: !!cropTypeId,
  });
};

export const useVarietyPricingById = (id: string) => {
  return useQuery({
    queryKey: ["varietyPricings", id],
    queryFn: () => varietyPricingApi.getVarietyPricingById(id),
    enabled: !!id,
  });
};

export const useCreateVarietyPricing = () => {
  return useMutation({
    mutationFn: (payload: CreateVarietyPricingRequest) =>
      varietyPricingApi.createVarietyPricing(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varietyPricings"] });
      queryClient.invalidateQueries({
        queryKey: ["varietyPricings", "active"],
      });
    },
  });
};

export const useUpdateVarietyPricing = () => {
  return useMutation({
    mutationFn: (payload: UpdateVarietyPricingRequest) =>
      varietyPricingApi.updateVarietyPricing(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["varietyPricings"] });
      queryClient.invalidateQueries({ queryKey: ["varietyPricings", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["varietyPricings", "active"],
      });
    },
  });
};

export const useDeleteVarietyPricing = () => {
  return useMutation({
    mutationFn: (id: string) => varietyPricingApi.deleteVarietyPricing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varietyPricings"] });
      queryClient.invalidateQueries({
        queryKey: ["varietyPricings", "active"],
      });
    },
  });
};

export const useUpsertBatchVarietyPricing = () => {
  return useMutation({
    mutationFn: (payload: UpsertBatchVarietyPricingRequest) =>
      varietyPricingApi.upsertBatchVarietyPricing(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varietyPricings"] });
      queryClient.invalidateQueries({ queryKey: ["varietyPricings", "active"] });
    },
  });
};
